import { useContext, useMemo } from "react";

import { DispatchContext, DispatchSidePanels, PanelConfig, State, StateContext } from "./reducer";

/**
 * Get the full side panels state
 */
export function useSidePanels(): State {
    const context = useContext(StateContext);

    if (context === undefined) {
        throw new Error("useSidePanels must be used within a SidePanelsProvider");
    }

    return context;
}

/**
 * Get the dispatch function for side panels actions
 */
export function useDispatchSidePanels(): DispatchSidePanels {
    const context = useContext(DispatchContext);

    if (context === undefined) {
        throw new Error("useDispatchSidePanels must be used within a SidePanelsProvider");
    }

    return context;
}

/**
 * Get a specific panel's config by ID
 */
export function usePanel(panelId: string): PanelConfig | undefined {
    const { panelRegistry } = useSidePanels();
    return panelRegistry[panelId];
}

/**
 * Check if a specific panel is currently open
 */
export function useIsPanelOpen(panelId: string): boolean {
    const { openPanels } = useSidePanels();
    return openPanels.includes(panelId);
}

/**
 * Get all currently open panels with their configs
 */
export function useOpenPanels(): PanelConfig[] {
    const { openPanels, panelRegistry } = useSidePanels();

    return useMemo(() => {
        return openPanels
            .map((id) => panelRegistry[id])
            .filter((config): config is PanelConfig => config !== undefined);
    }, [openPanels, panelRegistry]);
}

/**
 * Check if any panels are open
 */
export function useHasOpenPanels(): boolean {
    const { openPanels } = useSidePanels();
    return openPanels.length > 0;
}

/**
 * Check if panels are in collapsed (half-height) mode
 */
export function useIsCollapsed(): boolean {
    const { collapsed } = useSidePanels();
    return collapsed;
}
